import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { delay, filter, map, tap } from 'rxjs/operators';

import { ColorModeService } from '@coreui/angular-pro';
import { IconSetService } from '@coreui/icons-angular';
import { iconSubset } from './icons/icon-subset';
import { AuthService } from './core/auth/auth.service';
import { IdleTimeoutModalComponent } from './shared/idle-timeout-modal/idle-timeout-modal.component';

@Component({
  selector: 'app-root',
  template: `
    <router-outlet />
    <om-idle-timeout-modal />
  `,
  standalone: true,
  imports: [RouterOutlet, IdleTimeoutModalComponent]
})
export class AppComponent implements OnInit {
  title = 'RingOffice Order Manager';

  readonly #destroyRef: DestroyRef = inject(DestroyRef);
  readonly #activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #titleService = inject(Title);
  readonly #authService = inject(AuthService);

  readonly #colorModeService = inject(ColorModeService);
  readonly #iconSetService = inject(IconSetService);
  private ssoPopupFinalized = false;

  constructor() {
    this.#titleService.setTitle(this.title);
    // iconSet singleton
    this.#iconSetService.icons = { ...iconSubset };
    this.#colorModeService.localStorageItemName.set('coreui-pro-angular-admin-template-theme-default');
    this.#colorModeService.eventName.set('ColorSchemeChange');
    this.applyPreferredTheme();
  }

  ngOnInit(): void {
    this.finalizeSsoPopupIfNeeded();

    this.#router.events.pipe(
        takeUntilDestroyed(this.#destroyRef)
      ).subscribe((evt) => {
      if (!(evt instanceof NavigationEnd)) {
        return;
      }
      this.finalizeSsoPopupIfNeeded();
    });

    this.#activatedRoute.queryParams
      .pipe(
        delay(1),
        tap(() => this.applyPreferredTheme()),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();

    window.addEventListener('storage', (event) => {
      if (['UserInfo', 'ro-theme', 'coreui-pro-angular-admin-template-theme-default'].includes(event.key || '')) {
        this.applyPreferredTheme();
      }
    });
  }

  private applyPreferredTheme(): void {
    let userInfo: any = {};
    try {
      userInfo = JSON.parse(localStorage.getItem('UserInfo') || '{}');
    } catch {
      userInfo = {};
    }
    const preferred = String(
      userInfo.theme ||
      userInfo.themePreference ||
      localStorage.getItem('ro-theme') ||
      localStorage.getItem('coreui-pro-angular-admin-template-theme-default') ||
      'light'
    ).toLowerCase();
    const mode = preferred === 'dark' ? 'dark' : 'light';
    this.#colorModeService.colorMode.set(mode);
    document.documentElement.dataset['coreuiTheme'] = mode;
  }

  private finalizeSsoPopupIfNeeded(): void {
    if (this.ssoPopupFinalized || typeof window === 'undefined') return;

    let isOrderManagerSsoPopup = false;
    try {
      isOrderManagerSsoPopup =
        window.name === 'onegate-sso' ||
        sessionStorage.getItem('om-sso-popup-flow') === '1';
    } catch {
      isOrderManagerSsoPopup = false;
    }

    if (!isOrderManagerSsoPopup || !this.#authService.isAuthenticated()) return;

    this.ssoPopupFinalized = true;
    this.notifySsoParent();

    [0, 150, 400, 900, 1400, 2200].forEach((delay) => {
      setTimeout(() => this.tryCloseCurrentWindow(), delay);
    });
  }

  private notifySsoParent(): void {
    try {
      if (window.opener) {
        window.opener.postMessage({ type: 'sso-complete' }, window.location.origin);
      }
    } catch {}

    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel('om-sso-token');
      bc.postMessage({ type: 'sso-complete' });
    } catch {}

    const lsKey = `om-sso-complete-${Date.now()}`;
    try {
      localStorage.setItem(lsKey, JSON.stringify({ type: 'sso-complete', ts: Date.now() }));
    } catch {}

    setTimeout(() => {
      try { bc?.close(); } catch {}
      try { localStorage.removeItem(lsKey); } catch {}
      try { sessionStorage.removeItem('om-sso-popup-flow'); } catch {}
    }, 1800);
  }

  private tryCloseCurrentWindow(): void {
    try { window.close(); } catch {}
    try { (window as any).self?.close?.(); } catch {}
    try {
      const w = window.open('', '_self');
      w?.close();
    } catch {}
  }
}
